<script module lang="ts">
  import type { BlogCategory, BlogPost, Pagination } from '@agapi/mlc-core';
  import type { RichtextStoryblok } from '@agapi/content-storyblok/storyblok';

  interface BlogCategoryProps {
    category: BlogCategory;
    posts: BlogPost[];
    pagination: Pagination;
    lang: string;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { PUBLIC_BRAND_NAME } from '$env/static/public';

  import { storyblokEditable } from '@storyblok/svelte';
  import { css } from '@agapi/ui/panda/css';
  import { Button } from '@agapi/ui/button';
  import { Loader } from '@agapi/ui/loader';
  import { Section } from '@agapi/ui/section';
  import { Breadcrumb, BreadcrumbItem } from '@agapi/ui/breadcrumb';
  import { Image } from '@agapi/ui/image';
  import { JsonLd } from '@agapi/ui/json-ld';
  import { AlternateLinks } from '@agapi/ui/alternate-links';

  import { blogPageStyles } from '../blog.styles';
  import { calculateReadingTime, parseIngress } from '../../../utils/storyblok.client';
  import { type BlogCardProps, BlogCard, BreadcrumbsSchema } from '../../../components';

  import * as m from '$lib/parachute/messages';
  import { getBlogSlug } from '../../../utils/blog.shared';
  import { rewriteUrl } from '../../../utils/locale.shared';
  import { getBlogPosts } from '../../../clients/storyblok.client';

  const { category, posts, pagination, lang = 'sv' }: BlogCategoryProps = $props();

  let allPosts = $state(posts ?? []);
  let loading = $state(false);
  let paginationState = $state(pagination);

  const loadMore = async () => {
    if (loading || allPosts?.length >= pagination.total) {
      return;
    }

    loading = true;
    paginationState.currentPage += 1;

    try {
      const { posts = [], pagination } = await getBlogPosts(fetch, {
        locale: lang,
        page: paginationState.currentPage,
        perPage: paginationState.perPage,
        categoryId: category.id!
      });

      paginationState = pagination;

      allPosts = [...allPosts, ...posts];
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      loading = false;
    }
  };

  const blogCardProps = (post: BlogPost) =>
    ({
      title: post.title,
      description: parseIngress(post.content as RichtextStoryblok[]),
      readingTime: calculateReadingTime(post.content as RichtextStoryblok[]),
      imageUrl: post.featuredImage?.url,
      imageAlt: post.featuredImage?.alt,
      slug: post.slug,
      publishedAt: post.publishedAt ?? '',
      category: post.categories?.[0]?.name || ''
    }) satisfies BlogCardProps;

  console.log('blog lang', lang);

  const blogSlug = getBlogSlug(lang);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name + ' - ' + PUBLIC_BRAND_NAME,
    description: category.description,
    url: `${page.url.origin}/${blogSlug}/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: posts?.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${page.url.origin}/${blogSlug}/${post.slug}`,
        item: {
          '@type': 'BlogPosting',
          headline: post.title,
          datePublished: post.publishedAt,
          author: {
            '@type': 'Person',
            name: post.author?.name || PUBLIC_BRAND_NAME
          }
        }
      }))
    }
  };

  const { section, sectionContainer, heading, subTitle, postGrid, loadMoreContainer } =
    blogPageStyles.raw();
</script>

<svelte:head>
  <title>{category.name} - {PUBLIC_BRAND_NAME}</title>
  <meta name="description" content={category.meta?.description ?? category.description} />
  <link rel="canonical" href={rewriteUrl(page, `/${blogSlug}/${category.slug}`)} />
  <meta property="og:title" content={category.name} />
  <meta property="og:description" content={category.meta?.description ?? category.description} />
  <meta property="og:image" content={category.image?.url} />
  <meta
    property="og:url"
    content={rewriteUrl(page, `${page.url.origin}/${blogSlug}/${category.slug}`)}
  />
  <meta property="og:type" content="website" />
</svelte:head>

<BreadcrumbsSchema
  items={[
    { name: m.blog(), item: rewriteUrl(page, `${blogSlug}`) },
    {
      name: category.name ?? '',
      item: rewriteUrl(page, `${blogSlug}/${category.slug}`)
    }
  ]}
/>

<JsonLd data={schemaData} />

<AlternateLinks links={category.alternates ?? {}} />

<Section
  as="header"
  rootStyles={section}
  containerStyles={sectionContainer}
  use={[[storyblokEditable, category]]}
>
  <Breadcrumb>
    <BreadcrumbItem index={0}>
      <a href={rewriteUrl(page, `${blogSlug}`)}>
        {m.blog()}
      </a>
    </BreadcrumbItem>
    <BreadcrumbItem index={1}>{category.name}</BreadcrumbItem>
  </Breadcrumb>
  <h1 class={css(heading)}>{category.name}</h1>
  {#if category.description}
    <p class={css(subTitle)}>{category.description}</p>
  {/if}

  {#if category?.image && category?.image?.url !== ''}
    <Image
      width={800}
      aspectRatio={16 / 9}
      src={category.image.url ?? ''}
      alt={category.image?.alt || category.name}
      css={{ maxWidth: '100%', height: 'auto', marginBottom: '4' }}
    />
  {/if}
</Section>

<Section
  rootStyles={section}
  containerStyles={sectionContainer}
  use={[[storyblokEditable, category]]}
>
  <div class={css(postGrid)}>
    {#each allPosts as post}
      <BlogCard {...blogCardProps(post)} />
    {/each}
  </div>

  {#if paginationState?.hasMore}
    <div class={css(loadMoreContainer)}>
      <Button onclick={loadMore} disabled={loading}>
        {loading ? m.loading() : m.loadMoreItems()}
        {#if loading}
          <Loader />
        {/if}
      </Button>
    </div>
  {/if}
</Section>
